import {
  AlignmentType,
  Document,
  HeightRule,
  ImageRun,
  PageBreak,
  Paragraph,
  Table,
  TableCell,
  TableRow,
  TextRun,
  VerticalAlign,
  WidthType,
} from 'docx';

/**
 * Отчёт об оказанных услугах в Word — по форме, которую принимает
 * бухгалтерия клиента.
 *
 * ПОЧЕМУ WORD, А НЕ PDF. Документ подписывают и вкладывают в акт: в нём
 * дописывают реквизиты, правят формулировку услуги, сверяют суммы. PDF
 * заставил бы пересобирать файл из-за запятой.
 *
 * ПОЧЕМУ ВЁРСТКА ТАБЛИЦЕЙ И КАРТИНКАМИ. Word не рисует — он вставляет.
 * Экран статистики приходит сюда картинкой (см. insights-canvas), а
 * строкам без цифр оставляется пустая рамка того же размера: скриншот
 * вставят руками, и вёрстка не поедет.
 */

/**
 * Наименование услуги одно на весь отчёт, а не на строку: в форме клиента
 * все 27 строк повторяют одну и ту же фразу из договора. Поэтому на
 * странице это одно поле, а в таблицу Word оно попадает в каждую строку.
 */
export const DEFAULT_SERVICE = 'Создание и размещение рекламно-информационных материалов';

/** Колонки отчёта, в том же порядке, что в форме клиента. */
export const ACT_COLUMNS = [
  '№',
  'Наименование услуги',
  'Медиаресурс',
  'Дата размещения',
  'Ссылка',
  'Формат',
  'Просмотры',
  'Охваты',
  'Стоимость',
] as const;

/** Ширины колонок в твипах: 1 см = 567, ширина текста на A4 с полями 1,5 см. */
const COLUMN_WIDTHS = [454, 1928, 1247, 964, 2155, 851, 851, 851, 907];
const TABLE_WIDTH = COLUMN_WIDTHS.reduce((a, b) => a + b, 0);

const PAGE = {
  size: { width: 11906, height: 16838 },
  margin: { top: 850, right: 850, bottom: 850, left: 850 },
};

/** Картинка экрана в документе — 7 см по ширине; в пикселях при 96 dpi. */
const SHOT_WIDTH_PX = 265;
const SHOT_WIDTH_DXA = 3969;
/** Пустая рамка — по пропорциям экрана поста: 923 × 1675. */
const PLACEHOLDER_HEIGHT_DXA = Math.round((SHOT_WIDTH_DXA * 1675) / 923);

const FONT = 'Aptos';
const BODY_SIZE = 24;
const TABLE_SIZE = 18;
const SMALL_SIZE = 16;

export type ActScreen = {
  data: Uint8Array;
  /** Натуральный размер картинки — по нему считаем высоту в документе. */
  width: number;
  height: number;
  type: 'jpg' | 'png';
};

export type ActDocumentRow = {
  service: string;
  resource: string;
  date: string;
  url: string;
  format: string;
  views: string;
  reach: string;
  cost: string;
  screen: ActScreen | null;
};

export type ActDocumentInput = {
  /** ««14» сентября 2026 года» — уже в том виде, в каком стоит в шапке. */
  from: string;
  to: string;
  rows: ActDocumentRow[];
  totals: { views: string; reach: string; cost: string };
};

function text(
  value: string,
  options: { bold?: boolean; size?: number; align?: (typeof AlignmentType)[keyof typeof AlignmentType]; color?: string } = {},
): Paragraph {
  return new Paragraph({
    alignment: options.align,
    spacing: { after: 0 },
    children: [
      new TextRun({
        text: value,
        bold: options.bold,
        size: options.size ?? TABLE_SIZE,
        color: options.color,
      }),
    ],
  });
}

function cell(value: string, index: number, options: { bold?: boolean } = {}): TableCell {
  // Цифры прижаты вправо, остальное — влево: так колонку сумм читают столбиком.
  const numeric = index >= 6 && /\d/.test(value);
  return new TableCell({
    width: { size: COLUMN_WIDTHS[index], type: WidthType.DXA },
    verticalAlign: VerticalAlign.CENTER,
    children: [
      text(value, {
        bold: options.bold,
        size: index === 1 || index === 4 ? SMALL_SIZE : TABLE_SIZE,
        align: numeric ? AlignmentType.RIGHT : AlignmentType.LEFT,
      }),
    ],
  });
}

function headerRow(): TableRow {
  return new TableRow({
    tableHeader: true,
    children: ACT_COLUMNS.map(
      (name, i) =>
        new TableCell({
          width: { size: COLUMN_WIDTHS[i], type: WidthType.DXA },
          verticalAlign: VerticalAlign.CENTER,
          children: [text(name, { bold: true, align: AlignmentType.CENTER })],
        }),
    ),
  });
}

/** Итоговая строка: подпись во всю ширину до нужной колонки, число — в ней. */
function totalRow(label: string, value: string, column: number): TableRow {
  const tail = ACT_COLUMNS.length - column - 1;
  return new TableRow({
    children: [
      new TableCell({
        columnSpan: column,
        verticalAlign: VerticalAlign.CENTER,
        children: [text(label, { bold: true, align: AlignmentType.RIGHT })],
      }),
      new TableCell({
        width: { size: COLUMN_WIDTHS[column], type: WidthType.DXA },
        verticalAlign: VerticalAlign.CENTER,
        children: [text(value, { bold: true, align: AlignmentType.RIGHT })],
      }),
      ...(tail > 0
        ? [new TableCell({ columnSpan: tail, children: [text('')] })]
        : []),
    ],
  });
}

function screenParagraph(screen: ActScreen): Paragraph {
  const height = Math.round((SHOT_WIDTH_PX * screen.height) / screen.width);
  return new Paragraph({
    spacing: { after: 120 },
    children: [
      new ImageRun({
        data: screen.data,
        type: screen.type,
        transformation: { width: SHOT_WIDTH_PX, height },
      }),
    ],
  });
}

/** Рамка под скриншот, который вставят руками: место занято, вёрстка цела. */
function placeholder(): Table {
  return new Table({
    width: { size: SHOT_WIDTH_DXA, type: WidthType.DXA },
    columnWidths: [SHOT_WIDTH_DXA],
    rows: [
      new TableRow({
        height: { value: PLACEHOLDER_HEIGHT_DXA, rule: HeightRule.EXACT },
        children: [
          new TableCell({
            width: { size: SHOT_WIDTH_DXA, type: WidthType.DXA },
            verticalAlign: VerticalAlign.CENTER,
            children: [
              text('Место для скриншота', { align: AlignmentType.CENTER, color: '808080' }),
            ],
          }),
        ],
      }),
    ],
  });
}

export function buildActDocument(input: ActDocumentInput): Document {
  const table = new Table({
    width: { size: TABLE_WIDTH, type: WidthType.DXA },
    columnWidths: COLUMN_WIDTHS,
    rows: [
      headerRow(),
      ...input.rows.map(
        (row, i) =>
          new TableRow({
            children: [
              cell(String(i + 1), 0),
              cell(row.service, 1),
              cell(row.resource, 2),
              cell(row.date, 3),
              cell(row.url, 4),
              cell(row.format, 5),
              cell(row.views, 6),
              cell(row.reach, 7),
              cell(row.cost, 8),
            ],
          }),
      ),
      totalRow('Общее количество просмотров:', input.totals.views, 6),
      totalRow('Общее количество охватов:', input.totals.reach, 7),
    ],
  });

  // Приложение: паблик, ссылка и экран — подряд, одним списком.
  const appendix = input.rows.flatMap((row) => [
    new Paragraph({
      spacing: { before: 200, after: 0 },
      children: [new TextRun({ text: row.resource || row.url, bold: true, size: BODY_SIZE })],
    }),
    new Paragraph({
      spacing: { after: 60 },
      children: [new TextRun({ text: row.url, size: SMALL_SIZE })],
    }),
    row.screen ? screenParagraph(row.screen) : placeholder(),
  ]);

  return new Document({
    styles: { default: { document: { run: { font: FONT, size: BODY_SIZE } } } },
    sections: [
      {
        properties: { page: PAGE },
        children: [
          new Paragraph({
            alignment: AlignmentType.CENTER,
            spacing: { after: 200 },
            children: [
              new TextRun({ text: 'ФОРМА ОТЧЕТА ОБ ОКАЗАННЫХ УСЛУГАХ', bold: true, size: BODY_SIZE }),
            ],
          }),
          new Paragraph({
            spacing: { after: 120 },
            children: [
              new TextRun({
                text: `За отчетный период с ${input.from} по ${input.to} Исполнителем оказаны следующие услуги:`,
                size: BODY_SIZE,
              }),
            ],
          }),
          table,
          new Paragraph({
            spacing: { before: 200, after: 0 },
            children: [
              new TextRun({
                text: 'К отчету прилагаются скриншоты размещений и иные подтверждающие документы.',
                size: BODY_SIZE,
              }),
            ],
          }),
          new Paragraph({
            spacing: { before: 120 },
            children: [
              new TextRun({
                text: `Общая стоимость оказанных услуг: ${input.totals.cost} тенге.`,
                bold: true,
                size: BODY_SIZE,
              }),
            ],
          }),
          new Paragraph({ children: [new PageBreak()] }),
          new Paragraph({
            alignment: AlignmentType.CENTER,
            spacing: { after: 120 },
            children: [
              new TextRun({ text: 'Приложение: скриншоты размещений', bold: true, size: BODY_SIZE }),
            ],
          }),
          ...appendix,
        ],
      },
    ],
  });
}
